export function buildDashboardModel(response = {}) {
  const snapshot = response.snapshot_json || {};
  const snapshotSections = Array.isArray(snapshot.sections) ? snapshot.sections : [];
  const responseCards = Array.isArray(response.cards) ? response.cards : [];
  const sourceSections = buildSourceSections(response.source_payload_json);
  const primarySections = responseCards.length > 0
    ? buildCardsArraySection(responseCards, response, snapshot)
    : buildDisplaySections(snapshotSections, sourceSections);
  const supplementalSections = responseCards.length > 0
    ? []
    : buildEmbeddedSupplementalSections(response, sourceSections);
  const sections = [...primarySections, ...supplementalSections];
  const cards = sections.flatMap((section) => section.cards || []);

  return {
    title: snapshot.title || response.title || 'AI Leads Report',
    summaryMarkdown: snapshot.summary_markdown || response.summary_markdown || '',
    presentation: snapshot.presentation || response.presentation || {},
    date: snapshot.date || snapshot.snapshot_date || response.snapshot_date,
    generatedAt: snapshot.generated_at || response.generated_at,
    timezone: snapshot.timezone || response.timezone,
    dashboardUrl: response.dashboard_url,
    counts: snapshot.counts || buildSectionCounts(sections),
    sections,
    cards,
  };
}

export function hasPriorityReport(response = {}) {
  const snapshot = response.snapshot_json || {};
  const report = response.source_payload_json?.priority_report;
  return isSectionCollection(snapshot.priority_sections) || Boolean(report && typeof report === 'object' && (
    Array.isArray(report.hot) || Array.isArray(report.getting_colder)
  ));
}

export function buildPriorityDashboardModel(response = {}, legacySections = []) {
  const report = response.source_payload_json?.priority_report || {};
  const snapshot = response.snapshot_json || {};
  const sourceSections = buildSourceSections(response.source_payload_json);
  const legacyLookup = buildLegacyCardLookup([...legacySections, ...sourceSections]);
  const sourcePrioritySections = buildPrioritySourceSections(report);
  const snapshotPrioritySections = normalizeSectionCollection(snapshot.priority_sections);
  const prioritySections = isSectionCollection(snapshot.priority_sections)
    ? buildGeneratedPrioritySections(snapshotPrioritySections, sourcePrioritySections, legacyLookup)
    : buildSourcePrioritySections(sourcePrioritySections, legacyLookup);
  const supplementalSections = buildEmbeddedSupplementalSections(
    response,
    sourceSections,
  );
  const sections = [...prioritySections, ...supplementalSections];

  return {
    title: snapshot.title || response.title || 'AI Leads Report',
    summaryMarkdown: snapshot.summary_markdown || response.summary_markdown || '',
    presentation: snapshot.presentation || response.presentation || {},
    date: report.date || snapshot.date || snapshot.snapshot_date || response.snapshot_date,
    generatedAt: snapshot.generated_at || response.generated_at,
    timezone: report.timezone || snapshot.timezone || response.timezone,
    dashboardUrl: response.dashboard_url,
    counts: report.counts || snapshot.priority_counts || snapshot.counts || buildSectionCounts(sections),
    sections,
    cards: sections.flatMap((section) => section.cards),
  };
}

export function buildEmbeddedSupplementalSections(response = {}, sourceSections) {
  const snapshot = response.snapshot_json || {};
  const sections = normalizeSectionCollection(snapshot.supplemental_sections);

  if (sections.length === 0) {
    return [];
  }

  return buildDisplaySections(
    sections,
    sourceSections || buildSourceSections(response.source_payload_json),
    { fallbackByIndex: false },
  );
}

function isSectionCollection(value) {
  return Array.isArray(value) || Boolean(value && typeof value === 'object');
}

function normalizeSectionCollection(value) {
  if (Array.isArray(value)) {
    return value
      .filter((section) => section && typeof section === 'object' && !Array.isArray(section))
      .map((section, index) => normalizeSnapshotSection(section, section.section_key || section.kind || `section-${index}`));
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  return Object.entries(value).flatMap(([key, section]) => {
    if (Array.isArray(section)) {
      return [normalizeSnapshotSection({ cards: section }, key)];
    }

    if (section && typeof section === 'object') {
      return [normalizeSnapshotSection(section, key)];
    }

    return [];
  });
}

function normalizeSnapshotSection(section, fallbackKey) {
  const key = section.section_key || section.kind || fallbackKey;

  return {
    ...section,
    kind: section.kind || key,
    section_key: section.section_key || key,
    title: section.title || titleize(key),
    cards: Array.isArray(section.cards) ? section.cards : [],
  };
}

function buildPrioritySourceSections(report = {}) {
  return ['hot', 'getting_colder'].map((key) => ({
    kind: key,
    section_key: key,
    title: titleize(key),
    cards: Array.isArray(report[key]) ? report[key] : [],
  }));
}

function buildSourcePrioritySections(sourceSections, legacyLookup) {
  return sourceSections.map((section) => ({
    ...section,
    cards: section.cards.map((row, cardIndex) => {
      const legacyCard = findLegacyCard(row, legacyLookup);
      const sourceCard = legacyCard
        ? fillMissingValues(legacyCard.sourceCard || legacyCard, row)
        : row;
      const normalizedCard = normalizeCard(sourceCard, legacyCard?.snapshotCard || {}, section, cardIndex);

      return {
        ...normalizedCard,
        priority: normalizePriority(row),
      };
    }),
  }));
}

function buildGeneratedPrioritySections(snapshotSections, sourceSections, legacyLookup) {
  return snapshotSections.map((snapshotSection, sectionIndex) => {
    const sourceSection = findSourceSection(snapshotSection, sourceSections, sectionIndex, false);
    const sourceCards = sourceSection?.cards || [];
    const sourceLookup = buildSourceCardLookup(sourceCards);
    const displaySection = {
      ...(sourceSection || {}),
      ...snapshotSection,
    };

    return {
      ...displaySection,
      cards: snapshotSection.cards.map((snapshotCard, cardIndex) => {
        const priorityRow = findSourceCard(snapshotCard, sourceLookup)
          || (!hasCardIdentity(snapshotCard) ? sourceCards[cardIndex] : null)
          || {};
        const legacyCard = findLegacyCard(snapshotCard, legacyLookup)
          || findLegacyCard(priorityRow, legacyLookup);
        const sourceCard = fillMissingValues(
          fillMissingValues(snapshotCard, legacyCard?.sourceCard || legacyCard || {}),
          priorityRow,
        );
        const normalizedCard = normalizeCard(sourceCard, snapshotCard, displaySection, cardIndex);

        return {
          ...normalizedCard,
          priority: normalizePriority(fillMissingValues(snapshotCard, priorityRow)),
        };
      }),
    };
  });
}

export function getActionState(card, copy = {}) {
  const action = card.action || {};
  const missingFields = Array.isArray(action.missing_fields) ? action.missing_fields : [];
  const channel = action.channel || card.channel || 'manual';
  const fieldAlert = buildCriticalFieldAlert(missingFields, channel, copy);

  return {
    channel,
    label: action.label || defaultActionLabel(channel, copy),
    url: action.url,
    messageText: channel === 'manual' ? null : firstPresent(action.message_text, extractMessageTextFromUrl(action.url, channel)),
    missingFields,
    fieldAlert,
    disabled: !action.url || Boolean(fieldAlert?.isBlocking),
  };
}

const FIELD_GROUPS = {
  email: {
    label: 'email address',
    fields: ['contact_email', 'email', 'email_address'],
  },
  phone: {
    label: 'phone number',
    fields: ['safe_whatsapp_number', 'whatsapp_number', 'phone', 'phone_number', 'mobile_phone', 'mobile'],
  },
  contact: {
    label: 'contact name',
    fields: ['contact_name', 'contact'],
  },
  organization: {
    label: 'company name',
    fields: ['organization_name', 'organization', 'company', 'company_name'],
  },
  product: {
    label: 'product name',
    fields: ['product_name', 'product'],
  },
};

const CRITICAL_FIELD_ORDER = ['email', 'phone', 'contact', 'organization', 'product'];

function buildCriticalFieldAlert(missingFields = [], channel = 'manual', copy = {}) {
  const missingGroups = CRITICAL_FIELD_ORDER.filter((groupKey) => hasMissingField(missingFields, groupKey));
  const normalizedChannel = slugValue(channel || 'manual');
  const relevantGroups = missingGroups.filter((groupKey) => isCriticalFieldForChannel(groupKey, normalizedChannel, missingGroups));

  if (relevantGroups.length === 0) {
    return null;
  }

  const missingLabels = relevantGroups.map((groupKey) => fieldGroupLabel(groupKey, copy));
  const hasEmail = relevantGroups.includes('email');
  const hasPhone = relevantGroups.includes('phone');
  const onlyEmail = relevantGroups.length === 1 && hasEmail;
  const onlyPhone = relevantGroups.length === 1 && hasPhone;
  const onlyEmailAndPhone = relevantGroups.length === 2 && hasEmail && hasPhone;

  if (onlyEmail) {
    return createFieldAlert(
      copy.missingEmailMessage || 'Email address is missing.',
      copy.missingEmailTooltip || 'Add an email address to allow sending a message to this lead.',
      missingLabels,
    );
  }

  if (onlyPhone) {
    return createFieldAlert(
      copy.missingPhoneMessage || 'Phone number is missing.',
      copy.missingPhoneTooltip || 'Add a WhatsApp-capable number to enable WhatsApp outreach.',
      missingLabels,
    );
  }

  if (onlyEmailAndPhone) {
    return createFieldAlert(
      copy.missingContactMessage || 'Contact information is incomplete.',
      copy.missingContactTooltip || 'Add an email address or a WhatsApp-capable phone number to enable outreach.',
      missingLabels,
    );
  }

  return createFieldAlert(
    copy.missingKeyMessage || 'This lead is missing key information.',
    missingLabels.join(', '),
    missingLabels,
  );
}

function createFieldAlert(message, tooltip, missingLabels) {
  return {
    message,
    tooltip,
    missingLabels,
    isBlocking: true,
  };
}

function hasMissingField(missingFields, groupKey) {
  const fieldNames = FIELD_GROUPS[groupKey]?.fields || [];
  return missingFields.some((field) => fieldNames.includes(slugValue(field).replace(/-/g, '_')));
}

function isCriticalFieldForChannel(groupKey, channel, missingGroups) {
  if (['contact', 'organization', 'product'].includes(groupKey)) {
    return true;
  }

  if (groupKey === 'email') {
    return channel === 'email' || (isManualChannel(channel) && missingGroups.includes('phone'));
  }

  if (groupKey === 'phone') {
    return ['whatsapp', 'wa'].includes(channel) || (isManualChannel(channel) && missingGroups.includes('email'));
  }

  return false;
}

function isManualChannel(channel) {
  return ['manual', 'check_owner', 'check-owner', 'unknown', ''].includes(channel);
}

export function buildSearchText(card) {
  const localizedPresentationValues = Object.values(card.presentation || {}).flatMap((presentation) => (
    presentation && typeof presentation === 'object'
      ? [presentation.summary, presentation.recommended_action, presentation.label]
      : []
  ));
  return [
    card.organization,
    card.contact,
    card.product,
    card.channel,
    card.summary,
    card.recommendedAction,
    card.tag,
    card.stage,
    card.status,
    card.latestNoteText,
    card.latestMeetingSummary,
    card.leadHealth?.status,
    card.leadHealth?.label,
    card.priority?.combinationCode,
    card.priority?.reportGroup,
    card.priority?.revenueStrategicPotentialCode,
    card.priority?.interestLevelCode,
    card.priority?.valuesAlignmentCode,
    ...localizedPresentationValues,
    ...(card.leadHealth?.reasons || []).map((reason) => reason?.label || reason),
    ...(card.reasonCodes || []),
  ].filter(Boolean).join(' ').toLowerCase();
}

export function buildSectionFromCardsResponse(response = {}, fallback = {}) {
  const snapshot = response.snapshot_json || {};
  const cards = Array.isArray(response.cards)
    ? response.cards
    : Array.isArray(snapshot.cards)
      ? snapshot.cards
      : [];

  return buildCardsArraySection(cards, response, snapshot, fallback)[0];
}

function buildCardsArraySection(cards, response = {}, snapshot = {}, fallback = {}) {
  const section = {
    kind: response.section_key || snapshot.section_key || fallback.kind || 'section',
    section_key: response.section_key || snapshot.section_key || fallback.section_key || fallback.kind || 'section',
    title: response.title || snapshot.title || fallback.title || 'Leads',
    summary: response.summary || snapshot.summary || fallback.summary,
  };

  return [{
    ...section,
    cards: cards.map((card, cardIndex) => normalizeCard(card, card, section, cardIndex)),
  }];
}

function buildDisplaySections(snapshotSections, sourceSections, { fallbackByIndex = true } = {}) {
  if (snapshotSections.length === 0) {
    return sourceSections.map((sourceSection) => ({
      ...sourceSection,
      cards: sourceSection.cards.map((sourceCard, cardIndex) => normalizeCard(sourceCard, {}, sourceSection, cardIndex)),
    }));
  }

  return snapshotSections.map((snapshotSection, sectionIndex) => {
    const snapshotCards = Array.isArray(snapshotSection.cards) ? snapshotSection.cards : [];
    const sourceSection = findSourceSection(snapshotSection, sourceSections, sectionIndex, fallbackByIndex);
    const sourceCards = sourceSection?.cards || [];
    const sourceLookup = buildSourceCardLookup(sourceCards);
    const displaySection = {
      ...(sourceSection || {}),
      ...snapshotSection,
    };

    return {
      ...displaySection,
      cards: snapshotCards.map((snapshotCard, cardIndex) => {
        const sourceCard = findSourceCard(snapshotCard, sourceLookup)
          || (!hasCardIdentity(snapshotCard) ? sourceCards[cardIndex] : null)
          || {};
        return normalizeCard(sourceCard, snapshotCard, displaySection, cardIndex);
      }),
    };
  });
}

function fieldGroupLabel(groupKey, copy) {
  const copyKeys = {
    email: 'missingEmail',
    phone: 'missingPhone',
    contact: 'missingContact',
    organization: 'missingOrganization',
    product: 'missingProduct',
  };
  return copy[copyKeys[groupKey]] || FIELD_GROUPS[groupKey].label;
}

function buildSourceCardLookup(cards = []) {
  const byComposite = new Map();
  const byReference = new Map();
  cards.forEach((card) => {
    const identity = cardIdentity(card);
    if (!identity.referenceKey && !identity.compositeKey) {
      return;
    }
    if (identity.compositeKey && !byComposite.has(identity.compositeKey)) {
      byComposite.set(identity.compositeKey, card);
    }
    if (identity.referenceKey) {
      const matches = byReference.get(identity.referenceKey) || [];
      matches.push(card);
      byReference.set(identity.referenceKey, matches);
    }
  });
  return { byComposite, byReference };
}

function findSourceCard(snapshotCard, lookup) {
  const identity = cardIdentity(snapshotCard);
  if (!identity.referenceKey && !identity.compositeKey) {
    return null;
  }
  const composite = identity.compositeKey && lookup.byComposite.get(identity.compositeKey);
  if (composite) {
    return composite;
  }
  if (!identity.referenceKey) {
    return null;
  }
  const referenceMatches = lookup.byReference.get(identity.referenceKey) || [];
  return referenceMatches.length === 1 ? referenceMatches[0] : null;
}

function hasCardIdentity(card) {
  const identity = cardIdentity(card);
  return Boolean(identity.compositeKey || identity.referenceKey);
}

function buildSourceSections(sourcePayload = {}) {
  const report = sourcePayload.report || {};
  let sections;

  if (Array.isArray(report.sections)) {
    sections = report.sections.map((section, index) => ({
      kind: section.section_key || section.kind || section.title || `section-${index}`,
      section_key: section.section_key || section.kind,
      title: section.title || section.kind || `Section ${index + 1}`,
      summary: section.summary,
      cards: Array.isArray(section.cards) ? section.cards : [],
    }));
  } else {
    sections = Object.entries(report)
      .filter(([, value]) => Array.isArray(value))
      .map(([key, value]) => ({
        kind: key,
        section_key: key,
        title: titleize(key),
        cards: value,
      }));
  }

  const existingKeys = new Set(sections.map(normalizeSectionKey));
  const supplementalSections = Object.entries(sourcePayload.supplemental_report || {})
    .filter(([, value]) => Array.isArray(value))
    .filter(([key]) => !existingKeys.has(normalizeSectionKey(key)))
    .map(([key, value]) => ({
      kind: key,
      section_key: key,
      title: titleize(key),
      cards: value,
    }));

  return [...sections, ...supplementalSections];
}

function findSourceSection(snapshotSection, sourceSections, sectionIndex, fallbackByIndex = true) {
  const candidates = [
    snapshotSection.section_key,
    snapshotSection.kind,
    slugValue(snapshotSection.title),
  ].filter(Boolean).map(slugValue);

  return sourceSections.find((sourceSection) => {
    const sourceCandidates = [
      sourceSection.section_key,
      sourceSection.kind,
      sourceSection.title,
    ].filter(Boolean).map(slugValue);
    return candidates.some((candidate) => sourceCandidates.includes(candidate));
  }) || (fallbackByIndex ? sourceSections[sectionIndex] : undefined);
}

function normalizeCard(sourceCard = {}, snapshotCard = {}, section = {}, cardIndex = 0) {
  const parsedTitle = parseSnapshotTitle(snapshotCard.title);
  const context = sourceCard.dynamic_message_context || snapshotCard.dynamic_message_context || {};
  const action = snapshotCard.action || sourceCard.action || sourceCard.ready_link || null;
  const leadHealth = snapshotCard.lead_health || sourceCard.lead_health || null;
  const channel = action?.channel || sourceCard.channel_hint || snapshotCard.channel_hint || context.preferred_channel || snapshotCard.channel || leadHealth?.signals?.channel_hint || 'manual';
  const organization = firstPresent(
    sourceCard.organization_name,
    sourceCard.organization,
    snapshotCard.organization_name,
    snapshotCard.organization,
    parsedTitle.organization,
    'Unknown organization'
  );
  const contact = firstPresent(
    sourceCard.contact_name,
    sourceCard.contact,
    snapshotCard.contact_name,
    snapshotCard.contact,
    parsedTitle.contact,
    'Unknown contact'
  );
  const product = firstPresent(
    sourceCard.product_name,
    sourceCard.product,
    snapshotCard.product_name,
    snapshotCard.product,
    parsedTitle.product,
    'Unknown product'
  );
  const identity = cardIdentity({
    ...fillMissingValues(sourceCard, snapshotCard),
    product_name: product,
  });

  return {
    id: identity.compositeKey || identity.referenceKey || `${section.kind || section.title || 'section'}-${cardIndex}`,
    leadKey: identity.compositeKey,
    bucket: section.title || section.kind || snapshotCard.bucket || sourceCard.bucket || 'Uncategorized',
    organization,
    contact,
    product,
    channel,
    summary: firstPresent(snapshotCard.summary, sourceCard.summary),
    recommendedAction: firstPresent(
      snapshotCard.recommended_action,
      snapshotCard.next,
      sourceCard.recommended_action,
      context.priority_action_text,
      context.recommended_next_step,
      'Review manually'
    ),
    tag: firstPresent(snapshotCard.tag, sourceCard.tag),
    reasonCodes: Array.isArray(sourceCard.reason_codes)
      ? sourceCard.reason_codes
      : Array.isArray(snapshotCard.reason_codes) ? snapshotCard.reason_codes : [],
    daysSinceActivity: firstPresent(sourceCard.days_since_activity, snapshotCard.days_since_activity),
    latestActivityDate: firstPresent(sourceCard.latest_activity_date, snapshotCard.latest_activity_date),
    nextFollowUpDueAt: firstPresent(sourceCard.next_follow_up_due_at, snapshotCard.next_follow_up_due_at),
    nextScheduledMeetingDate: firstPresent(sourceCard.next_scheduled_meeting_date, snapshotCard.next_scheduled_meeting_date),
    stage: firstPresent(sourceCard.likely_stage_code, snapshotCard.likely_stage_code, parsedStage(snapshotCard.state)),
    status: firstPresent(sourceCard.process_status_code, snapshotCard.process_status_code, parsedStatus(snapshotCard.state)),
    businessStatus: firstPresent(sourceCard.custom_business_status_code, snapshotCard.custom_business_status_code),
    guidanceConsidered: firstPresent(
      snapshotCard.guidance_considered,
      sourceCard.guidance_considered,
    ) === true,
    latestNoteText: context.latest_note_text,
    latestMeetingSummary: context.recent_interaction_summary,
    leadHealth,
    action,
    presentation: snapshotCard.presentation || {},
    snapshotCard,
    sourceCard,
    section,
  };
}

function buildLegacyCardLookup(sections = []) {
  const byComposite = new Map();
  const byReference = new Map();

  sections.forEach((section) => {
    (section.cards || []).forEach((card) => {
      const identity = cardIdentity(card.sourceCard || card);
      if (!identity.referenceKey && !identity.compositeKey) {
        return;
      }

      if (identity.compositeKey && !byComposite.has(identity.compositeKey)) {
        byComposite.set(identity.compositeKey, card);
      }

      if (identity.referenceKey) {
        const referenceMatches = byReference.get(identity.referenceKey) || [];
        referenceMatches.push(card);
        byReference.set(identity.referenceKey, referenceMatches);
      }
    });
  });

  return { byComposite, byReference };
}

function findLegacyCard(priorityRow, lookup) {
  const identity = cardIdentity(priorityRow);
  if (!identity.referenceKey && !identity.compositeKey) {
    return null;
  }

  const compositeMatch = identity.compositeKey && lookup.byComposite.get(identity.compositeKey);
  if (compositeMatch) {
    return compositeMatch;
  }

  const referenceMatches = identity.referenceKey
    ? lookup.byReference.get(identity.referenceKey) || []
    : [];
  return referenceMatches.length === 1 ? referenceMatches[0] : null;
}

function cardIdentity(card = {}) {
  const leadKey = normalizeIdentityPart(card.lead_key);
  const referenceType = normalizeIdentityPart(card.lead_reference_type || 'lead');
  const referenceId = normalizeIdentityPart(card.lead_reference_id);
  const product = normalizeIdentityPart(card.product_name || card.product);
  const referenceKey = referenceId ? `${referenceType}:${referenceId}` : null;

  return {
    referenceKey,
    compositeKey: leadKey || (referenceKey && product ? `${referenceKey}:${product}` : null),
  };
}

function normalizeIdentityPart(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function fillMissingValues(fallback = {}, preferred = {}) {
  const merged = { ...fallback };

  Object.entries(preferred).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      merged[key] = value;
    }
  });

  return merged;
}

function normalizePriority(row = {}) {
  const priority = row.priority || {};

  return {
    combinationCode: priority.combination_code,
    rank: priority.rank,
    reportGroup: priority.report_group,
    revenueStrategicPotentialCode: row.revenue_strategic_potential_code,
    interestLevelCode: row.interest_level_code,
    valuesAlignmentCode: row.values_alignment_code,
  };
}

export function buildSectionCounts(sections = []) {
  return sections.reduce((counts, section) => {
    const key = normalizeSectionKey(section);
    const count = Array.isArray(section.cards) ? section.cards.length : 0;
    counts[key] = (counts[key] || 0) + count;
    if (key !== 'open_threads') {
      counts.open_threads = (counts.open_threads || 0) + count;
    }
    return counts;
  }, {});
}

export function normalizeSectionKey(sectionOrValue = {}) {
  const values = typeof sectionOrValue === 'object' && sectionOrValue !== null
    ? [sectionOrValue.section_key, sectionOrValue.kind, sectionOrValue.title]
    : [sectionOrValue];
  const candidates = values.filter(Boolean).map((value) => ({
    raw: String(value).toLowerCase(),
    key: slugValue(value).replace(/-/g, '_'),
  }));

  if (candidates.some(({ raw, key }) => raw.includes('getting colder') || raw.includes('enfriando') || key.includes('getting_colder'))) {
    return 'getting_colder';
  }

  if (candidates.some(({ raw, key }) => raw.includes('hot') || raw.includes('a punto de cerrar') || key === 'hot')) {
    return 'hot';
  }

  if (candidates.some(({ key }) => key.includes('watchlist'))) {
    return 'watchlist';
  }

  if (candidates.some(({ raw, key }) => key.includes('on_hold') || raw.includes('on hold'))) {
    return 'on_hold_threads';
  }

  return candidates[0]?.key || 'open_threads';
}

function parseSnapshotTitle(title) {
  if (!title || typeof title !== 'string') {
    return {};
  }

  const parts = title.split('/').map((part) => part.trim()).filter(Boolean);
  return {
    organization: parts[0],
    contact: parts[1],
    product: parts.slice(2).join(' / ') || undefined,
  };
}

function parsedStage(state) {
  return typeof state === 'string' ? state.split('|')[0]?.trim() : undefined;
}

function parsedStatus(state) {
  return typeof state === 'string' ? state.split('|')[1]?.trim() : undefined;
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function extractMessageTextFromUrl(url, channel) {
  if (!url || channel === 'manual') {
    return null;
  }

  try {
    const parsedUrl = new URL(url);
    const text = parsedUrl.searchParams.get('text') || parsedUrl.searchParams.get('body');
    return text || null;
  } catch (error) {
    return null;
  }
}

function titleize(value) {
  return String(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function slugValue(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function defaultActionLabel(channel, copy = {}) {
  if (channel === 'whatsapp') {
    return copy.openWhatsApp || 'Open WhatsApp';
  }
  if (channel === 'email') {
    return copy.openEmail || 'Open email';
  }
  return copy.reviewManually || 'Review manually';
}
